// ConnectionStatus
//
// Thin top-of-page banner that surfaces network state. Three states:
//
//   * OFFLINE     — red banner: "You're offline. Changes will save when you reconnect."
//   * SLOW        — amber banner: "Slow connection — pages may take a moment."
//                   Detected via navigator.connection.effectiveType in {2g, slow-2g}.
//   * UPDATE_RDY  — indigo banner: a new service-worker version was installed.
//                   "A new version is ready — reload to use it." button reloads.
//   * (idle/online + fast) — render nothing.
//
// Also pings a tiny URL on visibility change to catch the case where
// navigator.onLine reports true but the network is actually broken
// (captive portal, DNS hijack, dead WiFi). If the ping fails we treat
// the user as offline for UX purposes.
//
// Designed to live at the very top of <AppShell> children so it sits
// above the announcement banner. Self-contained — no context required.

import React, { useEffect, useState, useCallback } from 'react';
import { WifiOff, RefreshCw, ZapOff } from 'lucide-react';

const PROBE_URL = '/favicon.svg';   // tiny, always available, same-origin
const PROBE_TIMEOUT_MS = 4000;

async function probeConnection() {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
    const res = await fetch(`${PROBE_URL}?_=${Date.now()}`, {
      method: 'HEAD',
      cache: 'no-store',
      signal: controller.signal,
    });
    clearTimeout(timeout);
    return res.ok;
  } catch {
    return false;
  }
}

function detectSlowConnection() {
  const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!c) return false;
  // saveData=true means the user has opted into a data-saver mode in OS settings.
  if (c.saveData) return true;
  // effectiveType is computed from RTT + downlink — '2g' / 'slow-2g' = slow.
  return c.effectiveType === '2g' || c.effectiveType === 'slow-2g';
}

export default function ConnectionStatus() {
  const [online, setOnline] = useState(() => navigator.onLine);
  const [slow, setSlow] = useState(() => detectSlowConnection());
  const [updateReady, setUpdateReady] = useState(false);
  const [updateRegistration, setUpdateRegistration] = useState(null);

  // Recheck connection via a real HTTP probe — navigator.onLine is famously
  // unreliable (it just reports whether the OS has a network interface).
  const recheck = useCallback(async () => {
    if (!navigator.onLine) { setOnline(false); return; }
    const reachable = await probeConnection();
    setOnline(reachable);
  }, []);

  useEffect(() => {
    const handleOnline  = () => recheck();
    const handleOffline = () => setOnline(false);
    const handleConnectionChange = () => setSlow(detectSlowConnection());
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') recheck();
    };

    window.addEventListener('online',  handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibility);

    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (conn?.addEventListener) conn.addEventListener('change', handleConnectionChange);

    // Initial probe on mount in case navigator.onLine is stale.
    recheck();

    // Poll lightly while the tab is hidden? No — that's wasteful. Only on
    // visibility change above.

    return () => {
      window.removeEventListener('online',  handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibility);
      if (conn?.removeEventListener) conn.removeEventListener('change', handleConnectionChange);
    };
  }, [recheck]);

  // Listen for a new service worker that's waiting to activate. Fires when
  // a deploy lands and the user has cached an older bundle. Production only:
  // in dev, CRA's hot-reload re-installs the SW on every code change which
  // would trigger an auto-reload loop.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return;
    if (!('serviceWorker' in navigator)) return;
    let mounted = true;
    navigator.serviceWorker.getRegistration().then((reg) => {
      if (!reg || !mounted) return;
      if (reg.waiting) {
        setUpdateReady(true);
        setUpdateRegistration(reg);
      }
      reg.addEventListener('updatefound', () => {
        const next = reg.installing;
        if (!next) return;
        next.addEventListener('statechange', () => {
          if (next.state === 'installed' && navigator.serviceWorker.controller) {
            // A new version finished installing AND there's already an active SW.
            setUpdateReady(true);
            setUpdateRegistration(reg);
          }
        });
      });
    });

    // NOTE: we intentionally do NOT auto-reload on `controllerchange`. The
    // reload only fires when the user explicitly clicks the "Reload" button
    // in the banner via `reloadForUpdate()` below. Auto-reload caused an
    // infinite loop with CRA's hot-reload in dev and could be jarring in
    // production too (a user mid-form would lose their input).

    return () => { mounted = false; };
  }, []);

  const reloadForUpdate = () => {
    if (updateRegistration?.waiting) {
      updateRegistration.waiting.postMessage({ type: 'docketflow.sw.skip_waiting' });
    }
    // Reload either way — if there was a waiting SW the skipWaiting message
    // takes effect on next load; if not, this just picks up the latest bundle.
    window.location.reload();
  };

  // ── Render priority: offline > update-ready > slow ────────────────────
  if (!online) {
    // Honest copy: we don't currently have an IndexedDB cache or write
    // queue, so changes attempted while offline will fail with an
    // error toast (not silently queued). Promising "saved locally"
    // when nothing is actually persisted would cause real data loss
    // for a user who trusted it. Real offline-mode support is on the
    // post-launch list — until then we tell the truth.
    return (
      <Banner tone="red" icon={WifiOff}>
        <strong>You're offline.</strong> You can keep reading what's already loaded, but new changes won't save until you reconnect.
      </Banner>
    );
  }

  if (updateReady) {
    return (
      <Banner
        tone="indigo"
        icon={RefreshCw}
        action={
          <button
            onClick={reloadForUpdate}
            className="ml-3 px-3 py-1 rounded-lg text-xs font-semibold bg-white/20 hover:bg-white/30 transition"
          >
            Reload
          </button>
        }
      >
        <strong>A new version is ready.</strong> Reload to use it.
      </Banner>
    );
  }

  if (slow) {
    return (
      <Banner tone="amber" icon={ZapOff}>
        <strong>Slow connection.</strong> Pages may take a moment to load.
      </Banner>
    );
  }

  return null;
}

const TONES = {
  red:    'bg-red-600 text-white',
  amber:  'bg-amber-500 text-white',
  indigo: 'bg-indigo-600 text-white',
};

function Banner({ tone, icon: Icon, children, action }) {
  return (
    <div className={`${TONES[tone] || TONES.red} px-4 py-2 text-xs sm:text-sm flex items-center justify-center gap-2 shadow-sm`}>
      <Icon size={14} className="flex-shrink-0" />
      <span className="text-center">{children}</span>
      {action}
    </div>
  );
}
